import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer, Receipt as ReceiptIcon, RotateCcw } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Combobox } from '@/components/ui/Combobox';
import { TextArea } from '@/components/ui/Input';
import { SectionSpinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/Table';
import { toast } from '@/components/ui/Toast';
import { shopsApi } from '@/services/endpoints/masterData';
import { collectionsApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { autoAllocate, computeBalance, validateDraft } from '@/domain/collectionEngine';
import { uuid } from '@/lib/utils';
import { PaymentRows } from '../components/PaymentRows';
import { AllocationRows } from '../components/AllocationRows';
import { BalanceBar } from '../components/BalanceBar';
import { ReceiptSlip } from '../components/ReceiptSlip';
import type { AllocationDraft, CollectableInvoice, PaymentDraft, Receipt } from '@/domain/types';

/**
 * The agent's collection screen — the whole user journey from the requirements
 * on one page: pick the shop, record what was handed over, split it across
 * that shop's pending invoices, issue the slip.
 *
 * The `clientUuid` is minted once when the form is reset and reused for every
 * submit attempt. That is what makes a retry after a dropped connection safe:
 * the server recognises the UUID and returns the original receipt instead of
 * booking the money a second time.
 */
export function CollectPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [clientUuid, setClientUuid] = useState(() => uuid());
  const [shopId, setShopId] = useState<number | null>(null);
  const [shopName, setShopName] = useState('');
  const [payments, setPayments] = useState<PaymentDraft[]>([]);
  const [allocations, setAllocations] = useState<AllocationDraft[]>([]);
  const [notes, setNotes] = useState('');
  const [issued, setIssued] = useState<Receipt | null>(null);

  const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
    queryKey: queryKeys.shops.invoices(shopId ?? 0, true),
    queryFn: async (): Promise<CollectableInvoice[]> => {
      const rows = await shopsApi.invoices(shopId!, true);
      return rows.map((row) => ({
        id: row.id,
        invoiceNo: row.invoiceNo,
        description: row.description,
        amountCents: row.amountCents,
        allocatedCents: row.allocatedCents,
        outstandingCents: row.outstandingCents,
        dueDate: row.dueDate,
        isOverdue: false,
      }));
    },
    enabled: shopId !== null,
  });

  const positiveAllocations = useMemo(
    () => allocations.filter((allocation) => allocation.amountCents > 0),
    [allocations],
  );

  const balance = useMemo(
    () => computeBalance(payments, positiveAllocations),
    [payments, positiveAllocations],
  );

  const issues = useMemo(
    () => validateDraft(payments, positiveAllocations, invoices ?? []),
    [payments, positiveAllocations, invoices],
  );

  const reset = () => {
    // A fresh UUID marks the start of a genuinely new visit; reusing the old
    // one would make the next receipt look like a retry of the last.
    setClientUuid(uuid());
    setShopId(null);
    setShopName('');
    setPayments([]);
    setAllocations([]);
    setNotes('');
    setIssued(null);
  };

  const issue = useMutation({
    mutationFn: () =>
      collectionsApi.create({
        clientUuid,
        shopId: shopId!,
        collectedAt: new Date().toISOString(),
        payments: payments.map((payment) =>
          payment.method === 'CASH'
            ? { method: 'CASH' as const, amountCents: payment.amountCents }
            : {
                method: 'CHEQUE' as const,
                amountCents: payment.amountCents,
                chequeNo: payment.chequeNo.trim(),
                chequeDate: payment.chequeDate,
                bankId: payment.bankId!,
              },
        ),
        allocations: positiveAllocations,
        notes: notes.trim() || undefined,
      }),
    onSuccess: (receipt) => {
      setIssued(receipt);
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.shops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.success(t('collection.issued', { receiptNo: receipt.receiptNo }));
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(error.message);
      } else {
        toast.error(t('errors.unknown'));
      }
    },
  });

  // --- Issued state: show the slip ----------------------------------------
  if (issued) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('collection.issued', { receiptNo: issued.receiptNo })}
          className="no-print"
          actions={
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => window.print()}
                leftIcon={<Printer className="h-4 w-4" />}
              >
                {t('collection.printSlip')}
              </Button>
              <Button onClick={reset} leftIcon={<RotateCcw className="h-4 w-4" />}>
                {t('collection.newCollection')}
              </Button>
            </div>
          }
        />

        <ReceiptSlip receipt={issued} />
      </div>
    );
  }

  // --- Entry state ---------------------------------------------------------
  const blockingIssues = issues.filter(
    (issue) => !issue.paymentKey && !issue.invoiceId,
  );

  return (
    <div className="space-y-4 pb-4">
      <PageHeader title={t('collection.new')} />

      <Card>
        <CardBody>
          <Combobox
            label={t('collection.selectCustomer')}
            value={shopId}
            initialLabel={shopName}
            onChange={(id, option) => {
              setShopId(id);
              setShopName(option?.label ?? '');
              // A different customer means different invoices; keeping the old
              // split would silently misassign money.
              setAllocations([]);
            }}
            onSearch={async (term) => {
              // `mineOnly` is redundant for an AGENT (the server scopes them
              // regardless) but makes the intent explicit for admins testing
              // the flow.
              const results = await shopsApi.suggest(term, true);
              return results.map((shop) => ({
                id: shop.id,
                label: shop.name,
                sublabel: shop.routeName ?? undefined,
              }));
            }}
            required
          />
        </CardBody>
      </Card>

      {shopId === null ? (
        <Card>
          <EmptyState
            icon={<ReceiptIcon className="h-6 w-6" />}
            title={t('collection.selectCustomerFirst')}
          />
        </Card>
      ) : isLoadingInvoices ? (
        <Card>
          <SectionSpinner />
        </Card>
      ) : !invoices || invoices.length === 0 ? (
        <Card>
          <EmptyState title={t('collection.noPendingInvoices')} />
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader title={t('collection.payments')} />
            <CardBody>
              <PaymentRows payments={payments} onChange={setPayments} issues={issues} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title={t('collection.allocations')} />
            <CardBody>
              <AllocationRows
                invoices={invoices}
                allocations={allocations}
                onChange={setAllocations}
                onAutoAllocate={() => setAllocations(autoAllocate(balance.collectedCents, invoices))}
                issues={issues}
                collectedCents={balance.collectedCents}
              />
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <TextArea
                label={t('collection.notes')}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                rows={2}
              />
            </CardBody>
          </Card>

          <BalanceBar balance={balance} />

          {blockingIssues.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <ul className="space-y-1 text-sm text-amber-800">
                {blockingIssues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`}>
                    {t(`collection.issues.${issue.code}` as never, {
                      ...issue.values,
                      // Amounts in messages are cents; render them as rupees.
                      amount:
                        typeof issue.values?.amount === 'number'
                          ? (issue.values.amount / 100).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                            })
                          : issue.values?.amount,
                    })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={reset}>
              {t('common.cancel')}
            </Button>
            <Button
              size="lg"
              onClick={() => issue.mutate()}
              isLoading={issue.isPending}
              // The receipt must balance exactly and be free of field errors.
              disabled={issues.length > 0 || !balance.isBalanced}
              leftIcon={<ReceiptIcon className="h-4 w-4" />}
            >
              {issue.isPending ? t('collection.issuing') : t('collection.issueReceipt')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
